import { SyntheticEvent, useContext, useEffect, useEffectEvent, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { Alert, Grid, Tab, Tabs, useTheme } from '@mui/material'
import { useSnackbar } from 'notistack'

import { AppContext, useHost } from '../../providers/app-provider'
import { useAuthentication } from '../../providers/auth-provider'
import Api from '../../utils/api'
import useDocumentTitle from '../../utils/hooks/useDocumentTitle'

import AdminTab from './components/admin-tab'
import DetailsTab from './components/details-tab'
import InformationSideBar from './components/information-sidebar'
import ADMIN_PERMISSIONS from './admin-permissions'
import PeopleContext from './context'
import useStyles from './index.styles'

const People = () => {
  const classes = useStyles

  const { userId: paramUserId } = useParams()
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const { auth, ownPermissions, hasInternalUserAccess, ownUser } = useAuthentication()
  const host = useHost()
  const { tenantId, pageRefreshCounter } = useContext(AppContext)

  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | undefined>(undefined)
  const [user, setUser] = useState<IUserType | undefined>(paramUserId ? undefined : ownUser)
  const [membershipStatus, setMembershipStatus] = useState<MembershipStatus | undefined>(undefined)

  const canAccessAdmin = ownPermissions.length > 0 && ADMIN_PERMISSIONS.every((adminPermission) => !!ownPermissions.find((p) => p.name === adminPermission))
  const tab = canAccessAdmin ? (searchParams.get('tab') ?? 'details') : 'details'
  const userId = paramUserId ? Number(paramUserId) : ownUser?.id

  useDocumentTitle(userId && user ? `${user.information?.first} ${user.information?.last} | Meshify Central` : 'Meshify Central')

  useEffect(() => {
    if (!['details', 'admin'].includes(tab)) {
      searchParams.delete('tab')
      setSearchParams(searchParams)
    }
  }, [tab, searchParams, setSearchParams])

  useEffect(() => {
    // redirect if don't have permissions to access admin tab
    if (tab === 'admin' && !hasInternalUserAccess && userId) {
      navigate(`/tenant/${tenantId}/people/${userId}`, { replace: true })
    }
  }, [tab, hasInternalUserAccess, navigate, userId, tenantId])

  const handleTabChange = (_event: SyntheticEvent, newValue: 'details' | 'admin') => setSearchParams({ ...searchParams, tab: newValue })

  const revokeMembership = async () => {
    const res = await Api.del(`${host}/api/authentication/membership?userId=${userId}`)

    if (res.ok) {
      setMembershipStatus('Deactivated')
      enqueueSnackbar('User deactivated successfully.')
    } else {
      enqueueSnackbar('Could not deactivate user.', { variant: 'error' })
    }
  }

  const fetchData = useEffectEvent(async () => {
    if (!host || !auth.user?.access_token) return
    setLoading(true)
    setError(undefined)
    setMembershipStatus(undefined)

    const res = await Api.get(`${host}/api/users/${userId}`)
    if (res.ok) {
      setUser(res.body)

      // ENHANCEMENT: Ask backend for this information in the same request as the user
      const resMembership = await Api.get(`${host}/api/authentication/membership?userId=${userId}`)
      if (resMembership.ok) {
        if (res.body.phone) {
          setMembershipStatus('Active')
        } else {
          setMembershipStatus('Invited')
        }
      } else if (resMembership.status === 404) {
        setMembershipStatus('Deactivated')
      } else enqueueSnackbar('Something went wrong and we could not check the membership status at this time. Please try again later')
    } else if (res.status === 404) setError('User not found')
    else setError('Something went wrong and we could not load the user at this time. Please try again later')

    setLoading(false)
  })
  useEffect(() => {
    fetchData()
  }, [userId, host, pageRefreshCounter])

  return (
    <PeopleContext.Provider value={{ user, setUser, membershipStatus, setMembershipStatus, revokeMembership, loading, error }}>
      <div style={classes.mainContainer}>
        <div style={classes.bannerImage(theme)} />
        <Grid
          container={true}
          sx={{
            gap: 2,
          }}
        >
          <InformationSideBar />
          <Grid sx={classes.gridTabsWrapper} size="grow">
            {error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <>
                <Tabs value={tab} onChange={handleTabChange} sx={classes.tabs}>
                  <Tab label="Details" value="details" sx={classes.tabButtonBaseRoot} />
                  {canAccessAdmin && hasInternalUserAccess && <Tab label="Admin" value="admin" sx={classes.tabButtonBaseRoot} />}
                </Tabs>
                {tab === 'details' && <DetailsTab />}
                {tab === 'admin' && canAccessAdmin && <AdminTab />}
              </>
            )}
          </Grid>
        </Grid>
      </div>
    </PeopleContext.Provider>
  )
}

export default People
